import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Artist Management', path: '/artist-management' },
    { name: 'About', path: '/about' },
  ];

  const handleNavClick = (path) => {
    setIsMobileMenuOpen(false);
    if (path.includes('#')) {
      const sectionId = path.split('#')[1];
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/90 backdrop-blur-xl border-b border-gray-200 shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo Text */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <span className="font-oswald uppercase text-xl md:text-2xl font-bold tracking-tight">
              <span className="text-gray-900">Loose Leaf</span>
              <span className="text-primary"> Media</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => handleNavClick(link.path)}
                className={`nav-link text-sm font-bold uppercase tracking-widest transition-colors ${
                  location.pathname === link.path
                    ? 'text-primary'
                    : 'text-gray-600 hover:text-primary'
                }`}
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
            <a
              href="https://calendly.com/atabdullah-mah/30min"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-call"
            >
              <Button className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 py-2 text-sm font-bold uppercase tracking-wide btn-scale">
                Book a Call
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-800 p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-gray-200 py-6" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => handleNavClick(link.path)}
                  className={`text-sm font-bold uppercase tracking-widest py-2 ${
                    location.pathname === link.path
                      ? 'text-primary'
                      : 'text-gray-600'
                  }`}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              <a
                href="https://calendly.com/atabdullah-mah/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2"
                data-testid="mobile-nav-book-call"
              >
                <Button className="w-full bg-primary text-white hover:bg-primary/90 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wide">
                  Book a Call
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
